/**
 * Sentry `beforeSend` PII filter for the admin SPA — Wave 2.33 BE-2.
 *
 * Mirrors the server-side copies in `apps/api` and `apps/worker`. Kept
 * per-workspace so each runtime owns its Sentry SDK version independently
 * and `packages/shared` stays Sentry-type-free.
 *
 * Browser specifics:
 *   * No `user.email` or `user.username` ever — only `id` survives.
 *   * Form payloads in `breadcrumbs[].data` are walked.
 *   * Local/session storage is never serialised by Sentry by default; we
 *     do not opt into `attachScreenshot` to keep PII surface narrow.
 */

import type { ErrorEvent } from '@sentry/react';

const DENY_LIST: ReadonlySet<string> = new Set([
  'password',
  'password_hash',
  'passwordhash',
  'old_password',
  'new_password',
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'jwt',
  'authorization',
  'cookie',
  'set-cookie',
  'session',
  'email',
  'phone',
  'phone_number',
  'card_number',
  'pan',
  'cvv',
  'cvc',
  // 2FA material (TASK-450). `/settings/security` holds the freshly minted
  // `secret_base32`, `qr_code_uri` and the ten `recovery_codes` in component
  // state — a render error there would otherwise ship them to Sentry via
  // `extra` / breadcrumbs. Mirrors the API-side deny-list.
  'totp_code',
  'recovery_code',
  'recovery_codes',
  'secret_base32',
  'qr_code_uri',
]);

const PREFIX_DENY_LIST: readonly string[] = ['payment_'];

const FILTERED = '[Filtered]';
const MAX_DEPTH = 16;

function isDenied(key: string): boolean {
  const k = key.toLowerCase();
  if (DENY_LIST.has(k)) return true;
  return PREFIX_DENY_LIST.some((prefix) => k.startsWith(prefix));
}

function redactValue(value: unknown, depth: number): unknown {
  if (depth >= MAX_DEPTH) return value;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((v) => redactValue(v, depth + 1));
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = isDenied(key) ? FILTERED : redactValue(val, depth + 1);
  }
  return out;
}

export function redactPii(event: ErrorEvent): ErrorEvent | null {
  if (event.user) {
    const { id, ip_address, ...rest } = event.user;
    const filteredRest: Record<string, unknown> = {};
    for (const key of Object.keys(rest)) {
      filteredRest[key] = FILTERED;
    }
    event.user = { id, ip_address, ...filteredRest };
  }
  if (event.request) {
    if (event.request.headers) {
      const headers: Record<string, string> = {};
      for (const [k, v] of Object.entries(event.request.headers)) {
        headers[k] = isDenied(k) ? FILTERED : v;
      }
      event.request.headers = headers;
    }
    if (event.request.cookies) {
      event.request.cookies = FILTERED as unknown as typeof event.request.cookies;
    }
    if (event.request.data !== undefined) {
      event.request.data = redactValue(event.request.data, 0);
    }
  }
  if (event.extra) {
    event.extra = redactValue(event.extra, 0) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = redactValue(event.contexts, 0) as typeof event.contexts;
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => {
      if (bc.data) {
        return { ...bc, data: redactValue(bc.data, 0) as typeof bc.data };
      }
      return bc;
    });
  }
  return event;
}
